/**
 * API Service
 * Axios instance with authentication interceptors
 */

import axios from 'axios';
import { API_URL } from '../config/api';
import authService from './authService';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Token is invalid or expired
      const currentPath = window.location.pathname;

      // Don't redirect if already on auth pages
      if (!currentPath.startsWith('/auth') && !currentPath.startsWith('/login')) {
        // Clear token and redirect to login
        authService.removeToken();
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Export the api instance
export default api;

/**
 * Helper methods for common API patterns
 */

// GET request
export const get = (url, config = {}) => api.get(url, config);

// POST request
export const post = (url, data = {}, config = {}) => api.post(url, data, config);

// PUT request
export const put = (url, data = {}, config = {}) => api.put(url, data, config);

// DELETE request
export const del = (url, config = {}) => api.delete(url, config);

// POST with form data (for file uploads)
export const postForm = (url, formData, config = {}) => {
  return api.post(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};
